import { Burger } from '@mantine/core';
import {
  ArrowLeftRight,
  ChartNoAxesCombined,
  Glasses,
  FlaskConical,
  GraduationCap,
  Headphones,
  Home,
  Languages,
  Laptop,
  LogOut,
  Store,
  Users,
  Video,
} from 'lucide-react';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthUser } from '../providers/AuthProvider.tsx';
import { performLogout } from '../services/userManager';
import { canAccessLegacyUsersAdmin } from '../config/env';
import classes from './LeftBar.module.css';

type LeftBarProps = {
  toggle: () => void;
  desktopExpanded: boolean;
  onDesktopHoverChange: (hovered: boolean) => void;
};

export function LeftBar({
  toggle,
  desktopExpanded,
  onDesktopHoverChange,
}: LeftBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const user = useAuthUser();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const showLabels = isMobile || desktopExpanded;

  const allowedResearchUserIds = new Set(['82', '66', '26', '35']);

  const data: any = [
    {
      link: '/',
      label: t('left_bar_home', 'Home'),
      accountType: [
        'enthusiast',
        'student',
        'professional',
        'enterprise',
        'enterprise_member',
      ],
      icon: Home,
    },
    {
      link: '/freeCourses',
      disabled: i18n.language !== 'it',
      label: t('left_bar_free_courses', 'Corsi liberi'),
      accountType: ['enthusiast', 'student'],
      icon: GraduationCap,
    },
    {
      link: '/proCourses',
      disabled: i18n.language !== 'it',
      label: t('left_bar_pro_courses', 'Corsi pro'),
      accountType: ['student', 'professional', 'enterprise_member'],
      icon: GraduationCap,
    },
    {
      link: '/devices',
      label: t('left_bar_devices', 'Dispositivi'),
      accountType: [
        'enthusiast',
        'student',
        'professional',
        'enterprise_member',
      ],
      icon: Laptop,
    },
    {
      link: '/sessions',
      label: t('left_bar_pro_apps', 'Sessioni VR'),
      accountType: ['student', 'professional', 'enterprise_member'],
      icon: Glasses,
    },
    {
      link: '/users',
      label: t('left_bar_users', 'Utenti'),
      accountType: ['student', 'professional', 'enterprise_member'],
      icon: Users,
    },
    {
      link: '/freeApps',
      label: t('left_bar_free_apps', 'App 2D'),
      accountType: [
        'enthusiast',
        'student',
        'professional',
        'enterprise_member',
      ],
      icon: Video,
    },
    {
      link: '/research',
      label: t('left_bar_research', 'Ricerca'),
      accountType: ['student', 'professional', 'enterprise_member'],
      icon: ChartNoAxesCombined,
      allowedUserIds: Array.from(allowedResearchUserIds),
    },
    {
      link: '/admin/users',
      label: t('left_bar_admin_users', 'Admin utenti'),
      roles: ['admin', 'moderator'],
      icon: Users,
      color: 'orange',
    },
    {
      link: '/admin/legacy-migration',
      label: t('left_bar_admin_legacy_migration', 'Migrazione legacy'),
      roles: ['admin', 'moderator'],
      icon: ArrowLeftRight,
      color: 'orange',
      legacyAdminOnly: true,
      activePathPrefix: '/admin/legacy',
    },
    {
      link: '/admin/products',
      label: t('left_bar_admin_products', 'Admin prodotti'),
      roles: ['admin', 'moderator'],
      icon: Store,
      color: 'orange',
    },
    {
      link: '/admin/videos',
      label: t('left_bar_admin_videos', 'Admin video'),
      roles: ['admin', 'moderator'],
      icon: Video,
      color: 'orange',
    },
    {
      link: '/admin/audios',
      label: t('left_bar_admin_audios', 'Admin audio'),
      roles: ['admin', 'moderator'],
      icon: Headphones,
      color: 'orange',
    },
    {
      link: '/admin/courses',
      label: t('left_bar_admin_courses', 'Admin corsi'),
      roles: ['admin', 'moderator'],
      icon: GraduationCap,
      color: 'orange',
    },
    {
      link: '/admin/translations',
      label: t('left_bar_admin_translations', 'Traduzioni'),
      roles: ['admin'],
      icon: Languages,
      color: 'orange',
    },
    {
      link: '/admin/app-playground',
      label: t('left_bar_admin_app_playground', 'App Playground testing'),
      roles: ['admin'],
      icon: FlaskConical,
      color: 'orange',
      activePathPrefix: '/admin/app-playground',
    },
  ];

  const filterItem = (item: any) => {
    if (item.legacyAdminOnly && !canAccessLegacyUsersAdmin(user?.email)) {
      return false;
    }
    const accountTypeMatch = item.accountType
      ? item.accountType.includes(user?.type ?? '')
      : true;
    const roleMatch = item.roles
      ? item.roles.some((role: string) => user?.roles?.includes(role))
      : true;
    const allowedUserMatch = item.allowedUserIds
      ? item.allowedUserIds.includes(String(user?.id ?? ''))
      : true;

    return accountTypeMatch && roleMatch && allowedUserMatch;
  };

  const isAdminItem = (item: any) => Boolean(item.roles);

  const isAdminUser = user?.roles?.includes('admin') ?? false;

  const mainItems = data
    .filter((item: any) => !isAdminItem(item))
    .filter(filterItem);
  const adminItems = isAdminUser
    ? data.filter(isAdminItem).filter(filterItem)
    : [];

  const renderLink = (item: any) => (
    <Link
      style={{
        opacity: item.disabled ? 0.5 : 1,
        pointerEvents: item.disabled ? 'none' : 'auto',
        backgroundColor: item.color === 'orange' ? '#fefce8' : undefined,
      }}
      data-active={
        item.activePathPrefix
          ? location.pathname.startsWith(item.activePathPrefix) || undefined
          : location.pathname === item.link || undefined
      }
      className={classes.link}
      data-expanded={showLabels ? 'true' : 'false'}
      to={item.link}
      key={item.label}
    >
      <item.icon
        size={24}
        color={item.color === 'orange' ? '#ffa500' : undefined}
        className={classes.linkIcon}
      />
      <span className={classes.linkLabel}>{item.label}</span>
    </Link>
  );

  const handleLogout = async () => {
    const serverLogoutSuccess = await performLogout();

    navigate('/login');

    if (!serverLogoutSuccess) {
      notifications.show({
        color: 'orange',
        withBorder: true,
        radius: 'lg',
        title: 'Warning',
        autoClose: 5000,
        message: 'Logged out locally, but server logout failed',
      });
    }
  };

  return (
    <nav
      className={classes.navbar}
      data-expanded={showLabels ? 'true' : 'false'}
      onMouseEnter={() => {
        if (!isMobile) {
          onDesktopHoverChange(true);
        }
      }}
      onMouseLeave={() => {
        if (!isMobile) {
          onDesktopHoverChange(false);
        }
      }}
    >
      <Burger opened={true} onClick={toggle} hiddenFrom='sm' size='sm' />

      <div
        className={classes.navbarMain}
        data-account-type={user?.type ?? 'none'}
      >
        {mainItems.map(renderLink)}
        {isAdminUser && adminItems.length > 0 && (
          <div className={classes.adminSection}>
            <span className={classes.adminSectionLabel}>
              {t('left_bar_admin_menu', 'Admin menu')}
            </span>
            <div
              className={classes.adminSeparator}
              role='separator'
              aria-hidden
            />
            {adminItems.map(renderLink)}
          </div>
        )}
        {isMobile && (
          <a
            className={classes.link}
            data-expanded='true'
            onClick={e => {
              e.preventDefault();
              handleLogout();
            }}
          >
            <LogOut size={24} className={classes.linkIcon} />
            <span className={classes.linkLabel}>
              {t('header_logout', 'Logout')}
            </span>
          </a>
        )}
      </div>

      <div className={classes.footer}>
        {!isMobile && (
          <a
            className={classes.link}
            data-expanded={showLabels ? 'true' : 'false'}
            onClick={e => {
              e.preventDefault();
              handleLogout();
            }}
          >
            <LogOut size={24} className={classes.linkIcon} />
            <span className={classes.linkLabel}>
              {t('header_logout', 'Logout')}
            </span>
          </a>
        )}
      </div>
    </nav>
  );
}
